import { useState, type ReactElement } from "react";
import {
  Alert,
  Button,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import type { Session } from "../../app/App";
import { useSettings, refreshSettings } from "../../data/hooks";
import { upsertSetting } from "../../data/api";

export const SettingsPage = ({
  session,
}: {
  readonly session: Session;
}): ReactElement => {
  const [error, setError] = useState<string | undefined>();
  const { client, user } = session;
  const settings = useSettings(client);
  const identifier = user.email ?? user.username ?? "Signed-in user";

  const showNotesDefault =
    settings.status === "ready" &&
    settings.data.get("show_notes_default") === "true";
  const [toggling, setToggling] = useState(false);

  const toggleNotes = async (): Promise<void> => {
    const next = String(!showNotesDefault);
    setToggling(true);
    try {
      await upsertSetting(client, "show_notes_default", next);
      if (settings.status === "ready") {
        const updated = new Map(settings.data);
        updated.set("show_notes_default", next);
        await refreshSettings(client, updated);
      }
    } finally {
      setToggling(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await client.logout();
      window.location.reload();
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Unable to log out.");
    }
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: 680, mx: "auto" }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
        Settings
      </Typography>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="overline" color="primary">
            Account
          </Typography>
          <Typography>Logged in as {identifier}</Typography>
          <Divider />
          <Button
            variant="outlined"
            color="error"
            onClick={() => void logout()}
          >
            Log out
          </Button>
          {error !== undefined && <Alert severity="error">{error}</Alert>}
        </Stack>
      </Paper>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="overline" color="primary">
            Preferences
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={showNotesDefault}
                onChange={() => void toggleNotes()}
                disabled={toggling || settings.status !== "ready"}
              />
            }
            label="Show notes by default in the picker"
          />
          <Divider />
          <Typography variant="caption" color="text.secondary">
            Controls whether name notes automatically expand when picking. Each
            card can still be toggled individually.
          </Typography>
        </Stack>
      </Paper>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={1}>
          <Typography variant="overline" color="primary">
            About
          </Typography>
          <Typography variant="h6">Baby Names</Typography>
          <Typography color="text.secondary">
            A private shared name picker.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Version 1.0.0
          </Typography>
        </Stack>
      </Paper>
    </Stack>
  );
};
